import { togglePolicyType } from '@/model/ltcFinancialPlanningModel';
import {
  POLICY_TYPE_CATEGORIES,
  PolicyTypeCard,
} from '@client/components/ltc-financial-planning/PolicyTypeCard';
import { AppState } from '@client/model/store';
import { FundingPolicyType } from '@shared/appModel';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { StepContentProps } from '../types';

export function PolicyTypesStep(props: StepContentProps) {
  const dispatch = useDispatch();
  const { selectedPolicyTypes, preferences } = useSelector(
    (state: { ltcFinancialPlanning: AppState['ltcFinancialPlanning'] }) =>
      state.ltcFinancialPlanning,
  );

  // Determine predicted policy types based on preferences
  const getPredictedPolicyTypes = (): FundingPolicyType[] => {
    const predicted: FundingPolicyType[] = [];

    // If they want death benefits, recommend life insurance products
    if (preferences.deathBenefit === 'yes') {
      predicted.push(FundingPolicyType.lifeInsuranceWithRider);
    }

    // If they prefer upfront payment, recommend hybrid/asset-based
    if (preferences.paymentStructure === 'upfront') {
      predicted.push(FundingPolicyType.hybridLifeInsurance);
      predicted.push(FundingPolicyType.annuityHybrid);
    }

    // If they prefer monthly payments, recommend traditional LTC
    if (preferences.paymentStructure === 'monthly') {
      predicted.push(FundingPolicyType.longTermCareInsurance);
    }

    // If they want guaranteed benefits, recommend hybrids
    if (preferences.benefitStructure === 'guaranteed') {
      predicted.push(FundingPolicyType.hybridLifeInsurance);
      predicted.push(FundingPolicyType.annuityHybrid);
    }

    // If they want max value, recommend traditional LTC
    if (preferences.benefitStructure === 'maxValue') {
      predicted.push(FundingPolicyType.longTermCareInsurance);
      predicted.push(FundingPolicyType.lifeInsuranceWithRider);
    }

    // Default recommendations if nothing specific
    if (predicted.length === 0) {
      predicted.push(
        FundingPolicyType.longTermCareInsurance,
        FundingPolicyType.lifeInsuranceWithRider,
        FundingPolicyType.hybridLifeInsurance,
      );
    }

    return [...new Set(predicted)];
  };

  const predictedTypes = useMemo(() => {
    return getPredictedPolicyTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences]);

  const handleTogglePolicyType = (policyTypeId: FundingPolicyType) => {
    dispatch(togglePolicyType(policyTypeId));
  };

  return (
    <div className="py-8">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">
          Based on your preferences...
        </h1>
        <p className="mx-auto mt-3 max-w-2xl text-base text-gray-700">
          We think these policy types would be a great fit. Feel free to adjust the selection to see
          different options.
        </p>
      </div>

      <div className="mx-auto mt-8 max-w-2xl space-y-3">
        {POLICY_TYPE_CATEGORIES.map(policyType => {
          const isSelected = selectedPolicyTypes.includes(policyType.id);
          const isPredicted = predictedTypes.includes(policyType.id);

          return (
            <PolicyTypeCard
              key={policyType.id}
              policyType={policyType}
              isSelected={isSelected}
              isPredicted={isPredicted}
              onToggle={() => handleTogglePolicyType(policyType.id)}
            />
          );
        })}
      </div>
    </div>
  );
}
